import { useCallback, useEffect, useRef } from 'react';
import { normalizeMediaUrl } from './normalize-media-url';

// Бэкенд отдаёт presigned-ссылки MinIO с коротким сроком жизни (сейчас 10 минут).
// Открытая страница переживает этот срок, и картинка отваливается с 403 Request has expired.
// Хук перезапрашивает данные, чтобы получить свежую подпись: по ошибке загрузки
// и при возврате на вкладку, если подпись уже могла протухнуть.

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;
const STALE_AFTER_MS = 8 * 60 * 1000;

type Options = {
  /** URL медиа из ответа API (с подписью). */
  url: string | null | undefined;
  /** Перезапрос данных — должен привести к новому url с новой подписью. */
  refresh: () => void;
};

export const useSignedMediaSrc = ({ url, refresh }: Options) => {
  const src = normalizeMediaUrl(url);

  const attemptsRef = useRef(0);
  const requestedForSrcRef = useRef<string | null>(null);
  const receivedAtRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    receivedAtRef.current = Date.now();
  }, [src]);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  // Ошибка загрузки: одна попытка обновления на каждую уникальную ссылку,
  // не больше MAX_RETRIES суммарно — иначе битая картинка зациклит перезапросы.
  const onError = useCallback(() => {
    if (!src) return;
    if (requestedForSrcRef.current === src) return;
    if (attemptsRef.current >= MAX_RETRIES) return;

    requestedForSrcRef.current = src;
    attemptsRef.current += 1;

    const delay = RETRY_DELAY_MS * attemptsRef.current;
    timeoutRef.current = window.setTimeout(refresh, delay);
  }, [src, refresh]);

  // Возврат на вкладку: подпись могла истечь, пока страница была скрыта.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - receivedAtRef.current < STALE_AFTER_MS) return;

      refresh();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refresh]);

  return { src, onError };
};
